// Given a flagged UTM row, produce a "suggested improved URL" that the
// user can copy/paste as a starting point. Pure: never fetches, never
// throws — returns null when we don't have enough to make a safer
// suggestion than the original (e.g. no base URL, unparseable).
//
// Strategy:
// - Source: lowercase the raw value; if a synonym resolves to a canonical
//   platform, use the lowercased canonical name (e.g. "IG" → "instagram").
// - Medium: if already canonical, keep. Else default to "organic-social"
//   (the most common safe canonical bucket for social).
// - Campaign/content/term/sourcePlatform: pass through as-is.
// - Base URL: use the row's `url` field (landingPagePlusQueryString). If
//   it's a relative path (starts with "/") we prefix the placeholder
//   origin https://example.com so the Builder structure is still
//   demonstrable. If `row.url` is missing entirely, return null — we
//   don't have enough to suggest anything useful.

import { buildUtmUrl } from "./build";
import { CANONICAL_MEDIUMS, SOURCE_TO_PLATFORM, type CanonicalMedium, type UtmRow } from "./types";

const DEFAULT_MEDIUM: CanonicalMedium = "organic-social";

function suggestedSource(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const key = raw.trim().toLowerCase();
  const canonical = SOURCE_TO_PLATFORM[key];
  if (canonical) return canonical.toLowerCase().replace(/\s+/g, "-");
  return key;
}

function suggestedMedium(raw: string | undefined): CanonicalMedium {
  if (!raw) return DEFAULT_MEDIUM;
  const key = raw.trim().toLowerCase();
  if ((CANONICAL_MEDIUMS as readonly string[]).includes(key)) return key as CanonicalMedium;
  return DEFAULT_MEDIUM;
}

export function suggestCanonicalUrl(row: UtmRow): string | null {
  const base =
    row.url && row.url.trim().startsWith("http")
      ? row.url
      : row.url
        ? `https://example.com${row.url.startsWith("/") ? row.url : `/${row.url}`}`
        : null;
  if (!base) return null;
  try {
    return buildUtmUrl(base, {
      source: suggestedSource(row.source),
      medium: suggestedMedium(row.medium),
      campaign: row.campaign,
      content: row.content,
      term: row.term,
      sourcePlatform: row.sourcePlatform,
    });
  } catch {
    return null;
  }
}
